<template>
    <div
        class="action-dropdown-wrapper"
        ref="dropdownRef"
    >
        <button
            class="action-dropdown-button"
            :aria-expanded="isOpen"
            aria-haspopup="true"
            aria-label="Actions menu"
            @click="toggleDropdown"
            @keydown="handleButtonKeyDown"
        >
            <span class="action-label">Actions</span>
            <al-icon
                :icon="isOpen ? 'chevron-up' : 'chevron-down'"
                class="action-icon"
            />
        </button>
        
        <div
            v-if="isOpen"
            class="action-dropdown-menu"
            role="menu"
        >
            <button
                v-if="selectedView !== 'timeline'"
                role="menuitem"
                tabindex="0"
                class="action-dropdown-item"
                :disabled="isExportDisabled"
                :aria-disabled="isExportDisabled"
                @click="handleExportClick"
                @keydown="handleItemKeyDown($event, 'export')"
            >
                Export table to CSV
            </button>
            <button
                role="menuitem"
                tabindex="0"
                class="action-dropdown-item"
                @click="handleAddRemoveClick"
                @keydown="handleItemKeyDown($event, 'addRemove')"
            >
                Add / Remove User Details
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref, onMounted, onBeforeUnmount, nextTick
} from 'vue';

interface Props {
    isExportDisabled?: boolean;
    selectedView?: string;
}

const props = withDefaults(defineProps<Props>(), {
    isExportDisabled: false,
    selectedView: 'table'
});

const emit = defineEmits<{
    export: [];
    addRemove: [];
}>();

const isOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const toggleDropdown = () => {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        nextTick(() => {
            const firstItem = dropdownRef.value?.querySelector('.action-dropdown-item:not([disabled])') as HTMLElement;
            firstItem?.focus();
        });
    }
};

const closeDropdown = () => {
    isOpen.value = false;
};

const handleExportClick = () => {
    if (!props.isExportDisabled) {
        emit('export');
        closeDropdown();
    }
};

const handleAddRemoveClick = () => {
    emit('addRemove');
    closeDropdown();
};

const shouldToggleDropdown = (key: string) => {
    return key === 'Enter' || key === ' ';
};

const shouldCloseDropdown = (key: string) => {
    return key === 'Escape' || (key === 'Tab' && isOpen.value);
};

const handleButtonKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    
    if (shouldToggleDropdown(key)) {
        event.preventDefault();
        toggleDropdown();
        return;
    }
    
    if (shouldCloseDropdown(key)) {
        closeDropdown();
        return;
    }
    
    if (key === 'ArrowDown' && !isOpen.value) {
        event.preventDefault();
        toggleDropdown();
    }
};

const focusNextItem = (currentItem: HTMLElement) => {
    const nextItem = currentItem.nextElementSibling as HTMLElement;
    if (nextItem && !nextItem.hasAttribute('disabled')) {
        nextItem.focus();
    }
};

const focusPreviousItem = (currentItem: HTMLElement) => {
    const prevItem = currentItem.previousElementSibling as HTMLElement;
    if (prevItem && !prevItem.hasAttribute('disabled')) {
        prevItem.focus();
    }
};

const executeAction = (action: 'export' | 'addRemove') => {
    if (action === 'export') {
        handleExportClick();
    } else {
        handleAddRemoveClick();
    }
};

const handleItemKeyDown = (event: KeyboardEvent, action: 'export' | 'addRemove') => {
    const { key } = event;
    const isEnterOrSpace = key === 'Enter' || key === ' ';
    
    if (isEnterOrSpace) {
        event.preventDefault();
        executeAction(action);
        return;
    }
    
    if (key === 'Escape') {
        event.preventDefault();
        closeDropdown();
        const button = dropdownRef.value?.querySelector('.action-dropdown-button') as HTMLElement;
        button?.focus();
        return;
    }
    
    if (key === 'Tab') {
        closeDropdown();
        return;
    }
    
    if (key === 'ArrowDown') {
        event.preventDefault();
        focusNextItem(event.target as HTMLElement);
        return;
    }
    
    if (key === 'ArrowUp') {
        event.preventDefault();
        focusPreviousItem(event.target as HTMLElement);
    }
};

const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
        closeDropdown();
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
.action-dropdown-wrapper {
    position: relative;
    display: inline-block;
    // Important: Remove border that parent .search-actions class adds
    border: none !important;
    border-radius: 0 !important;
    background: transparent !important;
    color: inherit !important;
    padding: 0 !important;
    
    // Preserve margin-left: auto from parent for positioning
    margin-left: auto;
}

:deep(.action-dropdown-button) {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #31708F;
    border: 1px solid #31708F !important;
    border-radius: 4px;
    background-color: #fff;
    height: 36px;
    padding: 0 12px 0 16px;
    cursor: pointer;
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 12.8px;
    transition: all 0.2s ease;
    white-space: nowrap;
    outline: none !important;
    box-shadow: none !important;
    
    &:hover,
    &[aria-expanded="true"] {
        background-color: #31708F;
        color: #fff;
        border: 1px solid #31708F !important;
        
        .action-label,
        .action-icon {
            color: #fff;
        }
    }
    
    &:focus-visible {
        outline: 2px solid #101010 !important;
        outline-offset: 2px;
        border: 1px solid #31708F !important;
    }
}

:deep(.action-label) {
    color: #31708F;
    transition: color 0.2s ease;
}

:deep(.action-icon) {
    color: #31708F;
    font-size: 16px;
    transition: color 0.2s ease;
}

:deep(.action-dropdown-menu) {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    background-color: white;
    border: 1px solid #E0E0E0;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 250px;
}

:deep(.action-dropdown-item) {
    display: block;
    width: 100%;
    padding: 12px 16px;
    text-align: left;
    border: none !important;
    background-color: white;
    font-family: Roboto;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #1A2021;
    cursor: pointer;
    transition: background-color 0.2s ease;
    outline: none !important;
    box-shadow: none !important;
    
    &:hover:not([disabled]),
    &:focus:not([disabled]) {
        background-color: #EEF1F1;
        border: none !important;
    }
    
    &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
    }
    
    &:first-child {
        border-radius: 4px 4px 0 0;
    }
    
    &:last-child {
        border-radius: 0 0 4px 4px;
    }
}
</style>
